import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import WebView, {WebViewMessageEvent} from 'react-native-webview';
import {ThemeProvider} from '@rneui/themed';
import {observer} from 'mobx-react';

import CatalogueAddToCartModal from '../../components/CatalogueAddToCartModal';
import CrossPlatformModal from '../../components/ui-controls/CrossPlatformModal';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import config from '../../config';
import {useModalDimensions} from '../../hooks/useModalDimensions';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {useCatalogue} from '../../queries/catalogue/useCatalogue';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import rewardsTheme from '../../theme/rewardsTheme';
import {logger} from '../../utils/logger';

type CatalogueEventData = {
  action?: string;
  productSKU?: string;
};

const isCatalogueEventData = (value: unknown): value is CatalogueEventData =>
  value !== null && typeof value === 'object' && !Array.isArray(value) && 'action' in value;

// In Web with WebView, you may see errors like this in the browser console:
//
// loader.min.js?version=x:1 Uncaught DOMException: Failed to execute 'pushState' on 'History':
// A history state object with URL 'http://localhost:7050/CATALOGUES/CATALOGUES_T/srcdoc?c=spring-the-season-of-wins-nsw-z1&page=3'
// cannot be created in a document with origin 'http://localhost:7050' and URL 'about:srcdoc'.
// at updateURL (https://nexuspublications.com.au/a10/publications/loader.min.js?version=x:1:15651)
// at https://nexuspublications.com.au/a10/publications/loader.min.js?version=x:1:3958
//
// This is because the Nexus script try to do `history.pushState` to update the URL with something
// like `?c=spring-the-season-of-wins-nsw-z3&page=1`. I don't think that URL update is needed here.

const CatalogueScreen = observer(() => {
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>();
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const {isWebDesktopScreen} = useWebScreenDimensions();

  // Dialog sizing: xlarge tier reserves room for ~3 horizontal product tiles so
  // the dialog presents as a roughly square card on desktop even when only one
  // product is shown. minWidth is pinned to width to stop the inner FlatList
  // from collapsing the dialog around a single 150px tile.
  const {width: dialogWidth, maxHeight: dialogMaxHeight} = useModalDimensions({
    size: 'xlarge',
    constrainHeight: true,
  });

  const {
    shopSelectionStore: {shopSalesforceId},
  } = useStores();

  // Hook suspends while loading (no manual loading state needed)
  const catalogueHtml = useCatalogue({storeId: shopSalesforceId});

  const platformStyles = StyleSheet.create({
    main: {
      ...(isWebDesktopScreen ? {} : {paddingHorizontal: 0}),
    },
  });

  const handleDialogClose = () => setIsDialogVisible(false);
  const handleWebViewMessage = (event: WebViewMessageEvent) => {
    // Extract data before logging to avoid React synthetic event reuse warning.
    // react-native-logs' safeStringify traverses all properties via JSON.stringify,
    // which triggers getters on the nullified synthetic event.
    const eventData = getWebViewEventData(event);
    logger.debug('CatalogueScreen', 'Received WebView message', {eventData});
    if (isCatalogueEventData(eventData) && eventData?.action === 'openProductView') {
      handleCatalogueMessage(eventData);
    }
  };

  const handleCatalogueMessage = (event: CatalogueEventData) => {
    logger.debug('CatalogueScreen', 'Received catalogue message', {event});
    if (event.productSKU) {
      const productSKUs = event.productSKU.split(',');
      setSelectedProductIds(productSKUs);
      setIsDialogVisible(true);
    } else logger.error('CatalogueScreen', 'No productSKU in the catalogue event data', {event});
  };

  return (
    <ThemeProvider theme={rewardsTheme}>
      <ContentView testID="catalogue-screen" style={platformStyles.main}>
        <WebView
          cacheEnabled={false}
          scrollEnabled
          startInLoadingState
          source={{
            html: catalogueHtml,
            baseUrl: config.HOST_DOMAIN,
          }}
          originWhitelist={['*']}
          domStorageEnabled={true}
          javaScriptEnabled={true}
          style={styles.webview}
          onMessage={handleWebViewMessage}
          webviewDebuggingEnabled={config.ENV === 'DEV' || config.ENV === 'BETA'}
        />

        <CrossPlatformModal
          isVisible={isDialogVisible}
          onBackdropPress={handleDialogClose}
          hasBackdrop
          backdropColor="black"
          backdropOpacity={0.5}
          onBackButtonPress={handleDialogClose}
          style={styles.modalStyle}>
          <View
            style={[
              styles.dialogOverlay,
              {width: dialogWidth, minWidth: dialogWidth, maxHeight: dialogMaxHeight},
            ]}>
            <Text style={styles.dialogTitle}>ADD TO CART</Text>
            <CatalogueAddToCartModal
              onClose={handleDialogClose}
              productIds={selectedProductIds || []}
            />
          </View>
        </CrossPlatformModal>
      </ContentView>
    </ThemeProvider>
  );
});

export default CatalogueScreen;

const styles = StyleSheet.create({
  webview: {
    flex: 1,
    height: '100%',
    width: '100%',
  },
  modalStyle: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  dialogOverlay: {
    backgroundColor: 'white',
    borderRadius: 4,
    padding: 20,
  },
  dialogTitle: {
    textAlign: 'center',
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colour.text.heading.primary,
    marginBottom: 12,
  },
});

const getWebViewEventData = (event: WebViewMessageEvent) => {
  let eventData;
  /**
   * Sometimes the WebView event data is a {@type string}
   *
   * This is likely because the react-native-webview for
   * IOS/Android can only send strings
   * {@link https://github.com/react-native-webview/react-native-webview/blob/master/docs/Reference.md#postmessagestr}
   */
  if (typeof event.nativeEvent.data === 'string') {
    try {
      eventData = JSON.parse(event.nativeEvent.data);
    } catch (error) {
      logger.warn('CatalogueScreen', 'Failed to parse WebView message data', {error});
      eventData = event.nativeEvent.data;
    }
  } else {
    eventData = event.nativeEvent.data;
  }
  return eventData;
};
